import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useTheme } from '@/providers';
import { decodeJwt } from '../../lib/utils/jwt';

interface PlainCustomerDetails {
  email?: string;
  emailHash?: string;
  fullName?: string;
  externalId?: string;
}

interface PlainConfig {
  appId: string;
  customerDetails?: PlainCustomerDetails;
  theme?: 'auto' | 'light' | 'dark';
  hideLauncher?: boolean;
  hideDoneThreads?: boolean;
  hideBranding?: boolean;
}

declare global {
  interface Window {
    Plain?: {
      init: (config: PlainConfig) => void;
      open: () => void;
      close: () => void;
      update: (config: Partial<PlainConfig>) => void;
      logout: () => void;
    };
  }
}

const PLAIN_SCRIPT_URL = 'https://chat.cdn-plain.com/index.js';
const CLAIMS_NAMESPACE = 'https://antioch.com/';

const PlainChat = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { resolvedTheme } = useTheme();
  const initializedRef = useRef(false);
  const scriptLoadedRef = useRef(false);
  const emailHashRef = useRef<string | null>(null);

  // Clear Plain session on unmount (logout)
  useEffect(() => {
    return () => {
      const plainLogout = window.Plain?.logout;
      if (typeof plainLogout === 'function') {
        plainLogout();
      }
    };
  }, []);

  // Extract HMAC email hash from access token claims and initialize widget
  useEffect(() => {
    const appId = import.meta.env.VITE_PLAIN_APP_ID;
    if (!appId || !isAuthenticated || !user) {
      return;
    }

    const initPlain = () => {
      if (!window.Plain) {
        return;
      }

      if (initializedRef.current) {
        window.Plain.update({
          theme: resolvedTheme === 'dark' ? 'dark' : 'light',
        });
        return;
      }

      window.Plain.init({
        appId,
        customerDetails: {
          email: user.email,
          emailHash: emailHashRef.current ?? undefined,
          fullName: user.name,
          externalId: user.sub,
        },
        theme: resolvedTheme === 'dark' ? 'dark' : 'light',
        hideLauncher: true,
        hideDoneThreads: true,
        hideBranding: true,
      });

      initializedRef.current = true;
    };

    // Read the email hash claim from the access token
    getAccessTokenSilently()
      .then((token) => {
        const payload = decodeJwt(token);
        const hash = payload?.[`${CLAIMS_NAMESPACE}plain_email_hash`] as string | undefined;
        emailHashRef.current = hash ?? null;
      })
      .catch((err) => {
        console.warn('Failed to read Plain email hash from token:', err);
      })
      .finally(() => {
        // Initialize the widget whether or not the hash was available
        if (window.Plain) {
          initPlain();
          return;
        }

        // Load the script dynamically if not already loading
        if (!scriptLoadedRef.current) {
          scriptLoadedRef.current = true;
          const script = document.createElement('script');
          script.src = PLAIN_SCRIPT_URL;
          script.async = true;
          script.onload = () => initPlain();
          script.onerror = () => {
            console.warn('Failed to load Plain chat script');
            scriptLoadedRef.current = false;
          };
          document.head.appendChild(script);
        }
      });
  }, [isAuthenticated, user, resolvedTheme, getAccessTokenSilently]);

  return null;
};

export default PlainChat;
